<script lang="ts" setup>
import { TravelOrderStatus, TravelOrderStatusMap } from "@/constants/trip";
import TripTimer from "@/views/trips/components/TripTimer.vue";
import type { TripMonitorOrder, TripMonitorRoute, TripMonitorTravelOrder } from "@/api/trips/dto/trip-monitor";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { useBrokerUpdate } from "@/composables";
import TooltipButton from "@/components/buttons/TooltipButton.vue";

import type { BrokerUpdateValidationContext, DisableRule } from "@/types/validation";

const { t } = useI18n();
const { createMonitorBrokerUpdateContext, createBrokerUpdateDisableRules, createSendUpdateDisableRules } =
  useBrokerUpdate();

const props = defineProps<{
  travelOrder: TripMonitorTravelOrder;
  index: number;
  route: TripMonitorRoute;
  tripId: number;
  orders: TripMonitorOrder[];
}>();

const emits = defineEmits<{
  (e: "updateMonitor"): void;
  (e: "showCreateDialog", travelOrder: TripMonitorTravelOrder): void;
  (e: "viewUpdate", updateId: number): void;
  (e: "openIntervalDialog", travelOrder: TripMonitorTravelOrder): void;
}>();

const relatedOrder = computed(() => {
  return props.orders.find((order) => order.id === props.travelOrder.orderId);
});

const brokerUpdateContext = computed<BrokerUpdateValidationContext>(() => {
  return createMonitorBrokerUpdateContext(props.route, props.travelOrder, props.orders);
});

const timerDisableRules = computed<DisableRule[]>(() => {
  return createBrokerUpdateDisableRules(brokerUpdateContext.value);
});

const sendUpdateDisableRules = computed<DisableRule[]>(() => {
  return createSendUpdateDisableRules(brokerUpdateContext.value);
});

const showTimer = computed(() => {
  if (props.route.technicalPartial) {
    return Boolean(brokerUpdateContext.value.isTravelOrderInProgress);
  }

  return Boolean(brokerUpdateContext.value.sendUpdates);
});

function getTravelOrderStatus(status: TravelOrderStatus) {
  return TravelOrderStatusMap.find((item) => item.id === status)?.name;
}
</script>

<template>
  <div class="d-flex align-center">
    <div class="font-size-12 text-grey-darken-2 mr-2" data-qa="travel-order-number">
      <span class="font-weight-500 mr-1">{{ t("general.short-travel-order") }} #{{ index + 1 }}</span>

      <span>({{ t("general.order") }}#{{ relatedOrder?.number }})</span>
    </div>

    <tooltip-button v-if="showTimer" :rules="timerDisableRules">
      <template #default="{ disabled }">
        <trip-timer
          :disabled="disabled"
          :interval="travelOrder.updateInterval"
          :next-update-time="travelOrder.nextUpdateTime"
          :truck-id="route.truck.id"
          :truck-number="route.truck.number"
          class="mr-2"
          @open-dialog="emits('openIntervalDialog', travelOrder)"
        ></trip-timer>
      </template>
    </tooltip-button>

    <template v-if="travelOrder.status === TravelOrderStatus.active">
      <div v-if="travelOrder.lastUpdateId" class="d-flex align-center">
        <permission-tooltip :permission="DispatcherPermissionValue.BrokerUpdatesList">
          <template #activator="{ disabled }">
            <v-btn
              :disabled="disabled"
              color="primary"
              size="xsmall"
              variant="text"
              @click="emits('viewUpdate', travelOrder.lastUpdateId)"
            >
              <template #prepend>
                <v-icon color="primary" size="small">mdi-eye-outline</v-icon>
              </template>

              <template #append>
                <span class="text-primary font-size-11 font-weight-600 ml-1">View update</span>
              </template>
            </v-btn>
          </template>

          {{ t("permission.view-broker-update") }}
        </permission-tooltip>
      </div>

      <template v-else>
        <tooltip-button :rules="sendUpdateDisableRules">
          <template #default="{ disabled }">
            <v-btn
              :disabled="disabled"
              class="font-size-11 font-weight-600"
              color="primary"
              data-qa="send-update-button"
              density="comfortable"
              size="small"
              variant="flat"
              @click="emits('showCreateDialog', travelOrder)"
            >
              Send update
            </v-btn>
          </template>
        </tooltip-button>
      </template>
    </template>

    <v-chip v-else class="font-size-11" color="red-lighten-2" density="comfortable" size="small" variant="flat">
      {{ getTravelOrderStatus(travelOrder.status) }}
    </v-chip>
  </div>
</template>

<style lang="scss" scoped></style>
